import {
  PageLoadingIndicator,
  PrivateProfilePage,
} from "@ecologi/react-components/src";
import { OmniForest } from "@ecologi/react-components/src/OmniForest";
import { ForestTilesList } from "@ecologi/react-components/src/ForestTilesList/ForestTilesList";
import { scrollToTileById } from "@ecologi/react-components/src/OmniForest/utils/scroll-to-tile-by-id";
import { getNumberOfTilesPerPage } from "@ecologi/react-components/src/OmniForest/utils/get-number-of-tiles-per-page";
import { ProfileAchievementsRow } from "@ecologi/react-components/src/ProfileAchievementsRow";
import { ProfileChecklist } from "@ecologi/react-components/src/ProfileChecklist";
import { ProfileImpact } from "@ecologi/react-components/src/ProfileImpact";
import { ProfileHeaderSection } from "@ecologi/react-components/src/ProfilePage";
import { ProfileProjects } from "@ecologi/react-components/src/ProfileProjects";
import { ReferralBanner } from "@ecologi/react-components/src/ReferralBanner";
import { SocialShare } from "@ecologi/react-components/src/SocialShare";
import {
  useBodyClass,
  useScrollToUrlHash,
} from "@ecologi/react-components/src/utils";
import {
  CursorDirection,
  CursorPagingOptions,
  hasFundedImpact,
  ImpactType,
  UserImpactAggregates,
  PlatformFeature,
  Tile,
  toPlural,
  User,
  UserType,
  Wordpress,
} from "@ecologi/shared";
import { useRouter } from "next/router";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import useWindowSize from "react-use/lib/useWindowSize";
import { Head } from "../../../components";
import {
  forkResult,
  getClientUrl,
  isBrowser,
  parseQueryObject,
} from "../../../lib";
import { useStore } from "../../../store";
import { useResumeSubscription } from "../hooks";
import {
  prefetchData,
  ProfileSSRProps,
  useChecklistItemHandlers,
  useEditForestTitle,
  usePaginatedTiles,
  useQueryAggregateOfCarbonOffsetsByProject,
  useQueryProfile,
  useQueryReferralImpact,
  useQueryAggregateOfCarbonRemovalsByProject,
  useQueryAggregateOfHabitatRestorationsByProject,
  useQueryPendingImpactTotals,
} from "./api";
import {
  getProfileImpactCounts,
  getProfileShareMetadata,
  getFormattedUserType,
} from "./utils";
import styled from "@emotion/styled";
import { Krystal } from "../custom-profiles/krystal";
import { dequal } from "dequal/lite";
import { getImpactReportCsv } from "../../../lib/api/get-impact-report-csv";
import useTileNavigation from "../hooks/use-tile-navigation";
import { getFlagValue } from "../../../lib/configcat";

const ProfilePageWrap = styled.div`
  position: relative;
`;

export const LAST_TILE_ID = "last";

function ProfileData({
  allProjects,
  profile: ssrProfile,
  usernameOrId,
  childUsernameOrId,
  accessToken,
  ...props
}: {
  // SSR load may come back null in the case of private/not found users
  allProjects: Wordpress.Project[];
  profile: UserType | null;
  usernameOrId: string;
  childUsernameOrId: string;
  accessToken: string;
}) {
  const router = useRouter();

  const { normalisedTileQuery, tileId } = useTileNavigation(ssrProfile.userId);

  const { location, user: myself, flags } = useStore();

  const isOwnProfile = myself?.userId === ssrProfile?.userId;
  const profile: User | undefined = useMemo(
    () => ssrProfile && getFormattedUserType(ssrProfile),
    [ssrProfile, isOwnProfile]
  );

  const { width: windowWidth } = useWindowSize();
  const forestTilesPerPage = getNumberOfTilesPerPage(windowWidth);

  // Current state of the cursor for 'infinite' pagination through forest
  const [tileCursor, setTileCursor] = useState<CursorPagingOptions>();
  // Current state of the paging if there are more/less results that can be fetched
  const [cursorStatus, setCursorStatus] = useState<{
    hasMoreOlder: boolean;
    hasMoreNewer: boolean;
  }>({
    hasMoreNewer: false, // profile.tiles will have the most recent
    hasMoreOlder: profile.tiles.length >= forestTilesPerPage,
  });

  // Profile will come back with an initial set of tiles
  const [tiles, setTiles] = useState<Tile[]>([...profile.tiles]);

  // If you visit someone else's profile, then click in the sidebar to view your own,
  // this ensures the tiles are correctly updated
  useEffect(() => {
    // The impact shop supplies mocked content to the current user stored in Zustand, but
    // not to the SSR profile. (The impact shop is the only location which navigates to
    // a profile using the LAST_TILE_ID const.)
    if (normalisedTileQuery === LAST_TILE_ID) {
      if (isOwnProfile) setTiles([...myself.tiles]);
      return;
    }

    if (!dequal(tiles, profile.tiles)) {
      setTiles([...profile.tiles]);
      setCursorStatus({
        hasMoreNewer: false,
        hasMoreOlder: profile.tiles.length >= forestTilesPerPage,
      });
    }
  }, [profile?.userId]);

  /**
   * Resets the forest back to the initial state returned from initial profile load
   */
  const goBackToTop = useCallback(() => {
    setTiles([...profile.tiles]);
    scrollToTileById(profile.tiles[0]?._id);
    setCursorStatus({
      hasMoreNewer: false,
      hasMoreOlder: profile.tiles.length >= forestTilesPerPage,
    });

    // Remove current query params from URL
    router.replace(profile.getProfilePath(), undefined, { shallow: true });
  }, [profile]);

  const referralImpact = useQueryReferralImpact(profile);

  // Check if tileId from query param exists, if not in initial set then request it from the API
  useEffect(() => {
    if (!tileId || normalisedTileQuery === LAST_TILE_ID) return;
    if (referralImpact.data?.trees.find((t) => t.userId === tileId)) {
      setCursorStatus({
        ...cursorStatus,
        hasMoreNewer: false,
      });
      return;
    }

    if (
      tiles.find((tile) => tile._id === tileId) === undefined &&
      !referralImpact.isLoading
    ) {
      // Clear initial set of trees from profile.tiles
      setTiles([]);
      setCursorStatus({ ...cursorStatus, hasMoreNewer: true });

      // Set tile cursor, will trigger a fetch of this page of tiles
      setTileCursor({
        id: normalisedTileQuery,
        direction: "after",
        isInclusive: true,
      });
    }
  }, [referralImpact.isLoading, tileId]);

  // Creates a function to manage paging either forwards/backwards through the forest
  const getTilePage = useCallback(
    (direction: CursorDirection) => () =>
      setTileCursor({
        direction: direction,
        // tiles ordered newest to oldest
        id: direction == "after" ? tiles[0]?._id : tiles[tiles.length - 1]?._id,
        isInclusive: true,
      }),
    [tiles]
  );

  // Handle fetching more trees when the tileCursor changes
  const paginatedTiles = usePaginatedTiles(
    profile.userId,
    forestTilesPerPage,
    tileCursor,
    true
  );

  // When the paginated tiles value changes, update the current tiles list with the
  // fetched tiles, either at the head or tail of the array depending on direction
  useEffect(() => {
    if (paginatedTiles.isLoading) return;
    if (paginatedTiles.data) {
      const { data, direction } = paginatedTiles.data;

      const cursorExistsInPaginatedTiles = !!data.find(
        (tile) => tile._id === tileCursor?.id
      );

      // Insert at head or tail depending on direction of fetch, remove duplicates
      const list = (
        direction == "after" ? [...data, ...tiles] : [...tiles, ...data]
      ).filter(
        (tree, idx, self) => idx === self.findIndex((t) => t._id === tree._id)
      );

      if (tileCursor?.id && !cursorExistsInPaginatedTiles) {
        // Then the cursor provided was not valid, and we're at the end of the list
        setCursorStatus({
          hasMoreNewer: true,
          hasMoreOlder: false,
        });
      }

      setTiles(list);

      // Handle hiding/showing the "Load more" tiles buttons at the top & bottom
      if (
        // Returned less than the max amount of results, so no more after this
        data.length < forestTilesPerPage ||
        // In the off chance someone has an exact multiple of forestTilesPerPage
        // use a dirty hack that relies upon the fact that if a response comes back
        // with a single tile that contains _only_ that of the cursor, then there are
        // no more results that can be fetched, and so we should hide the button
        // however, there'll be a double click of the button before it's hidden
        (data.length == 1 && data[0]._id == tileCursor.id)
      ) {
        direction == "after"
          ? setCursorStatus({ ...cursorStatus, hasMoreNewer: false })
          : setCursorStatus({ ...cursorStatus, hasMoreOlder: false });
      }
    }
  }, [paginatedTiles.isLoading, paginatedTiles.data, tileCursor?.id]);

  // Check if we've ever scrolled to a tile before, so we don't scroll around the
  // place if the tileId changes (from opening a modal) / got next page of tiles
  const [hasScrolledToTileId, setHasScrolledToTileId] = useState(
    normalisedTileQuery == undefined
  );

  // Scrolls directly to a tile when a fresh page load happens, as long as a `tileId` has
  // been supplied in the URL query params
  useEffect(() => {
    if (!tiles.length) return;

    if (hasScrolledToTileId) return;
    // We need to wait for subforests to load (`referralImpact`), otherwise the page
    // scrolls too early and then subsequently jumps to the wrong position (especially in
    // circumstances where a profile holds a significant number of subforest tiles)
    if (referralImpact.isLoading) return;

    if (normalisedTileQuery === LAST_TILE_ID) {
      setHasScrolledToTileId(true);
      scrollToTileById(tiles[0]._id);
    }

    if (
      tileId &&
      // Ensure the tile actually exists in the set before we try and scroll to it
      tiles.find((tile) => tile._id == tileId)
    ) {
      setHasScrolledToTileId(true);
      scrollToTileById(tileId);
    }
  }, [tiles, referralImpact.isLoading, tileId]);

  const handleResumeSubscription = useResumeSubscription();
  const onEditTitle = useEditForestTitle();
  const {
    handleAddChecklistItem,
    handleRemoveChecklistItem,
    handleToggleChecklistItem,
  } = useChecklistItemHandlers();

  const sharedProps = {
    isOwnProfile,
    profile: isOwnProfile ? myself : profile,
    referralData: referralImpact.data,
    tiles,
  };

  useBodyClass("bodyDefaultProfile");

  const {
    CARBON_OFFSETS: fundedCarbonOffsetsTotal,
    TREES: fundedTreesTotal,
    CARBON_REMOVAL: fundedCarbonRemovalsTotal,
    HABITAT_RESTORATION: fundedHabitatRestorationsTotal,
  } = getProfileImpactCounts(sharedProps.profile, referralImpact.data);

  const { shareTitle, shareDescription } = getProfileShareMetadata(
    sharedProps.profile,
    referralImpact.data
  );

  let twitterShareText = "Check out my Ecologi profile";

  if (sharedProps.profile.isBusinessPlan) {
    twitterShareText = `Check out ${toPlural(
      sharedProps.profile.groupName
    )} Ecologi profile`;
  } else if (sharedProps.profile.isFamilyPlan) {
    twitterShareText = isOwnProfile
      ? "Check out my family’s Ecologi profile"
      : `Check out ${toPlural(sharedProps.profile.groupName)} Ecologi profile`;
  } else if (!isOwnProfile) {
    twitterShareText = `Check out ${sharedProps.profile.fullNamePlural} Ecologi profile`;
  }

  twitterShareText = encodeURI(twitterShareText);

  const isKrystal = profile.username === "krystal";

  const userImpactAggregates: UserImpactAggregates = {
    [ImpactType.CARBON_OFFSETS]: useQueryAggregateOfCarbonOffsetsByProject(
      sharedProps.profile.userId
    ),

    [ImpactType.CARBON_REMOVAL]: useQueryAggregateOfCarbonRemovalsByProject(
      sharedProps.profile.userId
    ),
    [ImpactType.HABITAT_RESTORATION]:
      useQueryAggregateOfHabitatRestorationsByProject(
        sharedProps.profile.userId
      ),
  };

  const pendingImpactTotals = useQueryPendingImpactTotals(
    sharedProps.profile.userId
  );

  const impactTotals = {
    [ImpactType.TREES]: {
      paid: fundedTreesTotal,
      pending: pendingImpactTotals.data?.[ImpactType.TREES] || 0,
    },
    [ImpactType.CARBON_OFFSETS]: {
      paid: fundedCarbonOffsetsTotal,
      pending: pendingImpactTotals.data?.[ImpactType.CARBON_OFFSETS] || 0,
    },
    [ImpactType.CARBON_REMOVAL]: {
      paid: fundedCarbonRemovalsTotal,
      pending: pendingImpactTotals.data?.[ImpactType.CARBON_REMOVAL] || 0,
    },
    [ImpactType.HABITAT_RESTORATION]: {
      paid: fundedHabitatRestorationsTotal,
      pending: pendingImpactTotals.data?.[ImpactType.HABITAT_RESTORATION] || 0,
    },
  };

  return (
    <>
      <ProfilePageWrap>
        <Head
          description={shareDescription}
          title={shareTitle}
          noIndex={!hasFundedImpact(profile)}
        />

        <ProfileHeaderSection
          {...props}
          {...sharedProps}
          allProjects={allProjects}
          onResumeSubscription={handleResumeSubscription}
          userImpactAggregates={userImpactAggregates}
          isLoading={pendingImpactTotals.isLoading}
          getImpactReportCsv={getImpactReportCsv}
          impactTotals={impactTotals}
          accessToken={accessToken}
        />

        <OmniForest
          {...props}
          {...sharedProps}
          onEditTitle={onEditTitle}
          user={myself}
        >
          <ForestTilesList
            {...props}
            {...sharedProps}
            allProjects={allProjects}
            asPath={router?.asPath}
            getOlderTiles={getTilePage("before")}
            goBackToTop={goBackToTop}
            hasMoreNewer={cursorStatus.hasMoreNewer}
            hasMoreOlder={cursorStatus.hasMoreOlder}
            tileIdToView={
              normalisedTileQuery === LAST_TILE_ID ? tiles[0]?._id : tileId
            }
            tiles={tiles}
            user={myself}
          />
        </OmniForest>

        {isOwnProfile && myself.isBusinessType && (
          <ReferralBanner.Business user={profile} referralId={myself.userId} />
        )}
        {isOwnProfile && !myself.isBusinessType && (
          <ReferralBanner.Consumer user={profile} referralId={myself.userId} />
        )}
        {!isOwnProfile && !myself && (
          <ReferralBanner.Guest user={profile} referralId={profile.userId} />
        )}
        {!isOwnProfile && myself && (
          <ReferralBanner.Peer user={profile} referralId={myself.userId} />
        )}

        {isKrystal && <Krystal />}

        {((profile.checklistItems || []).length > 0 || isOwnProfile) && (
          <ProfileChecklist
            user={sharedProps.profile}
            isOwnProfile={isOwnProfile}
            onAddChecklistItem={handleAddChecklistItem}
            onToggleChecklistItem={handleToggleChecklistItem}
            onRemoveChecklistItem={handleRemoveChecklistItem}
          />
        )}
        {/* Only show the badges if they want to show it or it's their own profile */}
        {(((sharedProps.profile.achievements.length > 0 ||
          sharedProps.profile.isMtpUser) &&
          !sharedProps.profile.isBadgesHiddenFromProfile) ||
          isOwnProfile) && (
          <ProfileAchievementsRow
            user={sharedProps.profile}
            isOwnProfile={isOwnProfile}
          />
        )}
        {fundedCarbonOffsetsTotal !== 0 && (
          <>
            <ProfileProjects
              userImpactAggregates={userImpactAggregates}
              user={sharedProps.profile}
              isOwnProfile={isOwnProfile}
            />
            <ProfileImpact
              user={sharedProps.profile}
              isOwnProfile={isOwnProfile}
              location={location}
            />
          </>
        )}
        <SocialShare
          shareLink={getClientUrl(`/${profile.username}`)}
          twitterShareText={twitterShareText}
        />
      </ProfilePageWrap>
    </>
  );
}

export function ProfilePage({
  allProjects,
  profile,
  accessToken,
}: ProfileSSRProps) {
  const router = useRouter();

  const { usernameOrId, childUsernameOrId } = parseQueryObject(router.query);

  useScrollToUrlHash();

  // Do we need to take account of the childUsernameOrId here as well ?
  useEffect(() => {
    if (!isBrowser()) return;

    /**
     * It is possible the profile page will be loaded incorrectly with
     * no `usernameOrId` value.
     *
     * In this eventually this guard will redirect to the homepage
     */
    if (!usernameOrId || usernameOrId === "undefined") {
      router.push(window.location.pathname || "/");
    }
  }, [usernameOrId, router]);

  const dataProps = {
    allProjects,
    profile,
    usernameOrId,
    childUsernameOrId,
    accessToken,
  };

  // If no SSR profile came back, attempt to load it at runtime (from API or
  // store in the case of private users who are logged in viewing their own profile)
  const profileResult = useQueryProfile(usernameOrId, childUsernameOrId);

  return (
    <>
      {profile ? (
        <ProfileData {...dataProps} />
      ) : (
        <>
          <Head
            description={"Loading profile..."}
            title={"Loading profile..."}
          />
          {forkResult<User | UserType>(
            {
              loading: () => <PageLoadingIndicator text="Loading profile..." />,
              data: (profile, extraProps) => (
                <ProfileData {...extraProps} profile={profile} />
              ),
              // At this point the profile must be private since either the API
              // failed to return a profile or the users local store didn't have
              // it either (in the instance of a private user viewing their own)
              error: (error) =>
                error == "Not Found" ? (
                  <PageLoadingIndicator text="Loading profile..." />
                ) : (
                  <>
                    <Head
                      description="This profile is private"
                      title="Private profile"
                    />

                    <PrivateProfilePage />
                  </>
                ),
            },
            profileResult,
            {
              allProjects,
              usernameOrId,
              childUsernameOrId,
              accessToken,
            }
          )}
        </>
      )}
    </>
  );
}

ProfilePage.prefetchData = prefetchData;
